import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { generatePath, Link, useLocation, useNavigate, useParams } from 'react-router-dom-v5-compat';

import config from '@/config';
import { getGetSharedSecretQueryKey, useShareSecret } from '@/modules/api/codegen';
import { actions as messagesActions } from '@/modules/messages';
import type { Secret, Share } from '@/modules/secrets/types';
import { Button } from '@/modules/ui';

import { AlertCard } from '../AlertCard';

export interface ExpiredSecretLinkAlertProps {
    id: Secret['id'];
}

export const ExpiredSecretLinkAlert = ({ id }: ExpiredSecretLinkAlertProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const queryClient = useQueryClient();

    const { id: shareId } = useParams<{ id: Share['id'] }>();
    const refreshExpiration = useShareSecret();

    return (
        <AlertCard
            title={{ id: 'sharedSecret.alert.expired.title' }}
            description={{ id: 'sharedSecret.alert.expired.description' }}
            footer={
                <>
                    <Link to={generatePath(config.routes.secret, { id })}>
                        <Button type='primary' disabled={refreshExpiration.isPending}>
                            <FormattedMessage id='sharedSecret.alert.expired.button.secret' />
                        </Button>
                    </Link>
                    <Button
                        type='default'
                        loading={refreshExpiration.isPending}
                        onClick={() => {
                            refreshExpiration.mutate(
                                { pathParams: { id } },
                                {
                                    onSuccess: async () => {
                                        await queryClient.invalidateQueries({
                                            queryKey: getGetSharedSecretQueryKey({ shareId: shareId! }),
                                        });
                                        navigate(pathname, { state: { validityRefreshed: true }, replace: true });
                                    },
                                    onError: () => {
                                        dispatch(
                                            messagesActions.displayErrorMessage({
                                                message: formatMessage({ id: 'sharedSecret.alert.expired.refresh.error' }),
                                                options: {
                                                    duration: 5,
                                                },
                                            }),
                                        );
                                    },
                                },
                            );
                        }}
                    >
                        <FormattedMessage id='sharedSecret.alert.expired.button.refresh' />
                    </Button>
                </>
            }
        />
    );
};
